import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { Car, Plus, CheckCircle2, XCircle, Loader2, Search } from 'lucide-react';
interface Vehicle {
  id: number;
  plate: string;
  vehicle_type: string;
  brand: string | null;
  model: string | null;
  color: string | null;
  is_active: boolean;
  created_at: string;
}
export function VehiclesListPage() {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  useEffect(() => {
    fetchVehicles();
  }, []);
  const fetchVehicles = async () => {
    setLoading(true);
    const {
      data,
      error
    } = await supabase.from('vehicles').select('*').order('created_at', {
      ascending: false
    });
    if (!error && data) {
      setVehicles(data);
    }
    setLoading(false);
  };
  const toggleActive = async (vehicleId: number, currentStatus: boolean) => {
    await supabase.from('vehicles').update({
      is_active: !currentStatus
    }).eq('id', vehicleId);
    fetchVehicles();
  };
  const filtered = vehicles.filter(v => v.plate?.toLowerCase().includes(search.toLowerCase()) || v.vehicle_type?.toLowerCase().includes(search.toLowerCase()));
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-6435">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-id="element-6436">
        <DriverAdminTabs data-id="element-6437" />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-6438">
          <div className="flex items-center gap-3" data-id="element-6439">
            <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-6440">
              <Car className="w-6 h-6 text-[#006847]" data-id="element-6441" />
            </div>
            <div data-id="element-6442">
              <h1 className="text-2xl font-bold" data-id="element-6443">Vehículos</h1>
              <p className="text-white/40 text-sm" data-id="element-6444">
                {vehicles.length} registrados
              </p>
            </div>
          </div>
          <button onClick={() => navigate('/admin/vehicles/new')} className="flex items-center justify-center gap-2 bg-[#006847] hover:bg-[#005a3d] text-white font-bold py-3 px-6 rounded-xl transition-all active:scale-95" data-id="element-6445">
            <Plus className="w-5 h-5" data-id="element-6446" />
            Nuevo Vehículo
          </button>
        </div>

        {/* Search */}
        <div className="relative" data-id="element-6447">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-6448" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por placa o tipo..." className="w-full bg-[#141414] border border-white/10 rounded-xl pl-12 pr-4 py-3.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847]" data-id="element-6449" />
        </div>

        {/* List */}
        {loading ? <div className="flex justify-center py-20" data-id="element-6450">
            <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-6451" />
          </div> : filtered.length === 0 ? <div className="text-center py-20" data-id="element-6452">
            <Car className="w-16 h-16 text-white/10 mx-auto mb-4" data-id="element-6453" />
            <p className="text-white/40 text-lg font-medium" data-id="element-6454">
              {search ? 'Sin resultados' : 'No hay vehículos registrados'}
            </p>
          </div> : <div className="space-y-3" data-id="element-6455">
            {filtered.map(vehicle => <div key={vehicle.id} className="bg-[#141414] border border-white/10 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-6456">
                {/* Info */}
                <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-6457">
                  <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center shrink-0" data-id="element-6458">
                    <Car className="w-6 h-6 text-white/50" data-id="element-6459" />
                  </div>
                  <div className="min-w-0" data-id="element-6460">
                    <div className="flex items-center gap-2" data-id="element-6461">
                      <h3 className="font-bold text-white font-mono text-lg" data-id="element-6462">
                        {vehicle.plate}
                      </h3>
                      <span className="text-xs font-bold bg-white/10 px-2 py-0.5 rounded uppercase tracking-wider text-white/70" data-id="element-6463">
                        {vehicle.vehicle_type}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white/50 mt-1" data-id="element-6464">
                      {vehicle.brand && <span data-id="element-6465">{vehicle.brand}</span>}
                      {vehicle.model && <span data-id="element-6466">{vehicle.model}</span>}
                      {vehicle.color && <span className="flex items-center gap-1" data-id="element-6467">
                          <div className="w-3 h-3 rounded-full border border-white/20" style={{
                    backgroundColor: vehicle.color.toLowerCase()
                  }} data-id="element-6468" />
                          {vehicle.color}
                        </span>}
                    </div>
                  </div>
                </div>

                {/* Status + Actions */}
                <div className="flex items-center gap-3 shrink-0 mt-4 sm:mt-0" data-id="element-6469">
                  <button onClick={() => toggleActive(vehicle.id, vehicle.is_active)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${vehicle.is_active ? 'bg-[#006847]/20 text-[#00a870] hover:bg-[#006847]/30' : 'bg-red-500/10 text-red-400 hover:bg-red-500/20'}`} data-id="element-6470">
                    {vehicle.is_active ? <>
                        <CheckCircle2 className="w-3.5 h-3.5" data-id="element-6471" /> Activo
                      </> : <>
                        <XCircle className="w-3.5 h-3.5" data-id="element-6472" /> Inactivo
                      </>}
                  </button>
                </div>
              </div>)}
          </div>}
      </div>
    </div>;
}